import React, { useEffect, useState } from "react";
import { apiAdminOrders, apiAdminUpdateOrderStatus, downloadAdminCsv } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  pending: "text-amber-700 bg-amber-100",
  paid: "text-blue-700 bg-blue-100",
  shipped: "text-indigo-700 bg-indigo-100",
  delivered: "text-green-700 bg-green-100",
  cancelled: "text-gray-700 bg-gray-100",
};

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];

const AdminOrders = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    setItems(await apiAdminOrders());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await apiAdminUpdateOrderStatus(id, status);
    toast.success("Status updated");
    load();
    if (viewing && viewing.id === id) setViewing({ ...viewing, status });
  };

  const printPackingSlip = (o) => {
    const itemsHtml = o.items.map((i) => `
      <tr>
        <td style="padding:8px 6px;border-bottom:1px solid #eee;">${i.name} <span style="color:#666;">(${i.size})</span></td>
        <td style="padding:8px 6px;border-bottom:1px solid #eee;text-align:right;color:#666;">× ${i.qty}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Packing Slip ${o.orderNumber}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:#0a0a0a;margin:32px;font-size:13px;}
  h1{font-size:22px;margin:0 0 4px;}
  .kicker{color:#b91c1c;font-size:10px;letter-spacing:0.32em;text-transform:uppercase;font-weight:700;}
  .row{display:flex;gap:32px;margin-top:24px;}
  .row > div{flex:1;}
  .lbl{font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#999;margin-bottom:4px;}
  table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}
  .footer{margin-top:32px;padding-top:16px;border-top:1px solid #ddd;color:#666;font-size:11px;}
  .total{font-weight:700;font-size:16px;text-align:right;margin-top:8px;}
</style></head><body>
  <div class="kicker">Certified Wellness · Packing Slip</div>
  <h1>${o.orderNumber}</h1>
  <div style="color:#666;">${new Date(o.createdAt).toLocaleString()}</div>
  <div class="row">
    <div>
      <div class="lbl">Ship To</div>
      <strong>${o.firstName} ${o.lastName}</strong><br/>
      ${o.address}<br/>
      ${o.city}, ${o.state} ${o.zip}<br/>
      ${o.country}
    </div>
    <div>
      <div class="lbl">Customer</div>
      ${o.email}<br/>${o.phone}
    </div>
  </div>
  <table>
    <thead><tr><th style="text-align:left;padding:8px 6px;border-bottom:2px solid #0a0a0a;">Item</th><th style="text-align:right;padding:8px 6px;border-bottom:2px solid #0a0a0a;">Qty</th></tr></thead>
    <tbody>${itemsHtml}</tbody>
  </table>
  <div class="total">Total · $${o.total.toFixed(2)}</div>
  <div class="footer">FOR LABORATORY RESEARCH USE ONLY · NOT FOR HUMAN OR ANIMAL CONSUMPTION · MUST BE 21+</div>
  <script>window.print();</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Operations</span>
          </div>
          <h1 className="font-display text-4xl font-semibold text-[#0a0a0a] tracking-tight">Orders</h1>
        </div>
        <button
          onClick={async () => {
            try { await downloadAdminCsv("orders"); toast.success("Orders exported"); }
            catch { toast.error("Export failed"); }
          }}
          className="inline-flex items-center gap-2 h-10 px-5 text-[11px] font-semibold uppercase tracking-[0.18em] border border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
          data-testid="export-orders-csv-btn"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <div className="mb-8" />

      {loading ? <div className="text-sm text-gray-500">Loading…</div> : items.length === 0 ? (
        <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">No orders yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fafbfc] border-b border-gray-200 text-[10px] uppercase tracking-[0.18em] text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Order</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-right px-4 py-3 font-semibold">Total</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="border-b border-gray-100 hover:bg-[#fafbfc] cursor-pointer" onClick={() => setViewing(o)}>
                  <td className="px-4 py-3 font-semibold text-[#0a0a0a]">{o.orderNumber}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{new Date(o.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-700">{o.firstName} {o.lastName}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{o.email}</td>
                  <td className="px-4 py-3 text-right font-semibold">${o.total.toFixed(2)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-semibold uppercase tracking-[0.18em] px-2 py-1 ${statusColor[o.status]}`}>{o.status}</span></td>
                  <td className="px-4 py-3 text-right text-[11px] text-[#0a0a0a] font-semibold uppercase tracking-[0.18em]">View</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <Dialog open onOpenChange={() => setViewing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
            <DialogHeader className="px-6 py-4 border-b border-gray-200">
              <DialogTitle className="font-display text-2xl font-semibold text-[#0a0a0a]">Order {viewing.orderNumber}</DialogTitle>
            </DialogHeader>
            <div className="p-6 space-y-5 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Status:</span>
                <select value={viewing.status} onChange={(e) => updateStatus(viewing.id, e.target.value)} className="h-9 px-3 border border-gray-300 bg-white text-sm">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Customer</div>
                  <div className="mt-1 font-medium text-[#0a0a0a]">{viewing.firstName} {viewing.lastName}</div>
                  <div className="text-gray-600">{viewing.email}</div>
                  <div className="text-gray-600">{viewing.phone}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Shipping</div>
                  <div className="mt-1 text-gray-700">{viewing.address}</div>
                  <div className="text-gray-700">{viewing.city}, {viewing.state} {viewing.zip}</div>
                  <div className="text-gray-700">{viewing.country}</div>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-2">Items</div>
                <table className="w-full text-sm">
                  <tbody>
                    {viewing.items.map((i) => (
                      <tr key={i.productId + i.size} className="border-b border-gray-100">
                        <td className="py-2">{i.name} <span className="text-gray-500">({i.size})</span></td>
                        <td className="py-2 text-right text-gray-500">× {i.qty}</td>
                        <td className="py-2 text-right font-semibold">${(i.price * i.qty).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pt-3 border-t border-gray-100 space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>${viewing.subtotal.toFixed(2)}</span></div>
                {viewing.discount > 0 && <div className="flex justify-between text-[#b91c1c]"><span>Discount {viewing.couponCode ? `(${viewing.couponCode})` : ""}</span><span>-${viewing.discount.toFixed(2)}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>${viewing.shipping.toFixed(2)}</span></div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-100"><span>Total</span><span className="text-[#b91c1c]">${viewing.total.toFixed(2)}</span></div>
              </div>
              {viewing.notes && <div className="pt-3 border-t border-gray-100"><div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">Notes</div><p className="text-gray-700">{viewing.notes}</p></div>}
              <div className="pt-4 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => printPackingSlip(viewing)}
                  className="inline-flex items-center gap-2 h-10 px-5 text-[11px] font-semibold uppercase tracking-[0.18em] border border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
                  data-testid="print-packing-slip-btn"
                >
                  <Printer className="h-4 w-4" /> Print Packing Slip
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminOrders;
